import React, {useCallback, useLayoutEffect, useMemo, useState} from "react";
import {Col, Row} from "reactstrap";
import Paper from "@mui/material/Paper";
import {ReactSortable} from "react-sortablejs";
import {Box, Card, Checkbox, Collapse, FormControlLabel, Input, TextField, Tooltip, Typography, useTheme} from "@mui/material";
import ColorPickerPopover from "../../../../../../../components/app-specific/color-picker-popover";
import {ReactComponent as NoColorIcon} from "../../../../../../../../assets/images/notifications/no-color.svg";
import {ReactComponent as NoColorIconSquare} from "../../../../../../../../assets/images/notifications/no-color-square.svg";
import {Add, AddOutlined, DragHandleRounded, DragIndicator, ExpandMore, RemoveOutlined, SortByAlpha} from "@mui/icons-material";
import IconButton from "../../../../../../../components/base/button/icon_button";
import Button from "../../../../../../../components/base/button/button";
import Utils, {createUUId, stringComparator} from "../../../../../../../../core/services/utils/utils";
import classnames from "classnames";
import Grid2 from "@mui/material/Unstable_Grid2";
import ProductInformationPopover from "../../../../../../../components/app-specific/draggable-popover/product-information-popover";
import {ReactComponent as CheckboxIcon} from "../../../../../../../../assets/images/check-box/checkbox.svg";
import {ReactComponent as ActiveCheckboxIcon} from "../../../../../../../../assets/images/check-box/checkbox-active.svg";

const PrintBrochureViewConfigureGroupsSection = ({
                                                     configureGroupsState,
                                                     setConfigureGroupsState,
                                                     updatingStep,
                                                 }) => {

    /**
     * Updates the groups list.
     * @type {function(*): void}
     */
    const updateGroups = useCallback((updater) => {
        setConfigureGroupsState(prevState => {
            const newGroups = typeof updater === 'function' ? updater(prevState.groups) : updater;
            return {
                ...prevState,
                groups: newGroups,
            };
        });
    }, [setConfigureGroupsState]);

    /**
     * Adds a new group.
     */
    const addGroup = useCallback(() => {
        const newGroup = {
            id: createUUId(),
            title: 'New Group',
            defaultTitle: false,
            colorCode: null,
            contrastColorCode: null,
            products: [],
        };
        updateGroups(prevState => [newGroup, ...prevState]);
        document.querySelector(`.configure-brochure-group-card[data-group-id="${newGroup.id}"]`)?.scrollIntoView({behavior: 'smooth'});
    }, [updateGroups]);

    /**
     * Updates a specific group's property.
     */
    const updateGroupProperty = useCallback((groupId, updates) => {
        updateGroups(prevState => {
            const found = prevState.find(g => g.id === groupId);
            if (!found) return prevState;
            const newGroup = typeof updates === 'function' ? updates(found) : {...found, ...updates};
            return prevState.map(g => (g.id === groupId) ? newGroup : g);
        });
    }, [updateGroups]);

    /**
     * Toggles between using group color or custom color for dividers.
     * @type {(function(*, *): void)}
     */
    const setCustomDividerColor = useCallback((_, checked) => {
        setConfigureGroupsState(prevState => ({
            ...prevState,
            dividerColorConfig: {
                ...prevState.dividerColorConfig,
                useGroupColor: !checked,
            }
        }))
    }, [setConfigureGroupsState])

    return (
        <Row className={'w-100 mb-5 pb-5 mb-3'}>
            <Col xs={12}>
                <p className={'mx-2 text-sm black mb-3'}>
                    Organize your products into groups for the brochure. You can add, remove, rename groups, change colors, and drag products between them.
                </p>
            </Col>
            <Col xs={12} className={'mb-5'}>
                <Paper className={'paper d-flex flex-column h-100 p-2'}>
                    <Grid2 container spacing={2} sx={{marginBlockEnd: 2, width: '100%'}}>
                        <Grid2 xs={12} md={true}>
                            <TextField
                                label="Poster Title"
                                variant="outlined"
                                fullWidth
                                size={'small'}
                                value={configureGroupsState.title}
                                disabled={updatingStep}
                                onChange={(e) => setConfigureGroupsState(prevState => ({
                                    ...prevState,
                                    title: e.target.value ?? "",
                                }))}
                            />
                        </Grid2>
                        <Grid2 xs={12} md={'auto'}>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexGrow: 0, flexShrink: 0, height: '100%'}}>
                                {
                                    !configureGroupsState.dividerColorConfig.useGroupColor &&
                                    <ColorPickerPopover
                                        color={configureGroupsState.dividerColorConfig.colorCode}
                                        onImmediateChange={() => void 0}
                                        onSelect={(c) => setConfigureGroupsState(prevState => ({
                                            ...prevState,
                                            dividerColorConfig: {
                                                ...prevState.dividerColorConfig,
                                                colorCode: c,
                                            }
                                        }))}
                                    >
                                        {(onEnter) =>
                                            <Tooltip title={
                                                <Box sx={{p: 1, maxWidth: 300}}>
                                                    {
                                                        configureGroupsState.dividerColorConfig.colorCode &&
                                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                                            <Typography variant={'body2'}>
                                                                Divider Color:
                                                            </Typography>
                                                            <div className={'color-circle'}>
                                                                <NoColorIcon className={'placeholder'}/>
                                                                <div className={'circle'} style={{'--color': configureGroupsState.dividerColorConfig.colorCode}}/>
                                                            </div>
                                                            <Typography variant={'body2'}>
                                                                {configureGroupsState.dividerColorConfig.colorCode}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    {
                                                        !configureGroupsState.dividerColorConfig.colorCode &&
                                                        <Typography variant={'body2'}>
                                                            No color selected.
                                                        </Typography>
                                                    }
                                                </Box>
                                            }>
                                                <Box
                                                    sx={{width: 35, height: 35, cursor: 'pointer', position: 'relative'}}
                                                    onClick={(...args) => {
                                                        if (updatingStep) return;
                                                        onEnter(...args);
                                                    }}
                                                >
                                                    <Box component={NoColorIconSquare} sx={{width: 32, minWidth: 32, height: 32}}/>
                                                    <Box sx={{
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius: 1,
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        backgroundColor: configureGroupsState.dividerColorConfig.colorCode
                                                    }}
                                                    />
                                                </Box>
                                            </Tooltip>
                                        }
                                    </ColorPickerPopover>
                                }
                                <FormControlLabel
                                    label="Custom Divider Color"
                                    labelPlacement={'start'}
                                    sx={{margin: 0}}
                                    control={
                                        <Checkbox
                                            size={'small'}
                                            sx={{padding: 0, paddingInlineStart: 0.5}}
                                            disableRipple
                                            checked={!(configureGroupsState.dividerColorConfig.useGroupColor ?? false)}
                                            onChange={setCustomDividerColor}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                        />
                                    }
                                />
                            </Box>
                        </Grid2>
                    </Grid2>
                    <div className={'w-100 mb-4 d-flex align-items-center justify-content-between'}>
                        <p className={'text-md font-weight-semi-bold'}>
                            Groups
                        </p>
                        <Button
                            className='d-flex align-items-center justify-content-center'
                            variant={'contained'}
                            color={'primary'}
                            disabled={updatingStep}
                            onClick={addGroup}
                        >
                            <Add className={'mr-2'}/>
                            <p>Add Group</p>
                        </Button>
                    </div>
                    <Grid2
                        container
                        spacing={2}
                        component={ReactSortable}
                        tag={Grid2}
                        className={'w-100'}
                        list={configureGroupsState.groups}
                        setList={updateGroups}
                        handle={'.group-handle'}
                        animation={210}
                    >
                        {configureGroupsState.groups.map(group => (
                            <Grid2 xs={12} md={6} lg={4} key={group.id}>
                                <GroupCard
                                    group={group}
                                    updateGroupProperty={updateGroupProperty}
                                    updatingStep={updatingStep}
                                />
                            </Grid2>
                        ))}
                    </Grid2>
                </Paper>
            </Col>
        </Row>
    );
}

const GroupCard = ({
                       group,
                       updateGroupProperty,
                       updatingStep
                   }) => {
    const theme = useTheme();
    const [collapsed, setCollapsed] = useState(true);
    const collator = useMemo(() => new Intl.Collator(undefined, {sensitivity: 'base', numeric: true, ignorePunctuation: true}), []);
    const isSorted = useMemo(() => {
        const items = group.products;
        for (let i = 1; i < items.length; i++) {
            if (collator.compare(items[i - 1].partNo ?? '', items[i].partNo ?? '') > 0) return false;
        }
        return true;
    }, [group.products, collator]);

    /**
     * Sorts products in a group alphabetically by Part No.
     */
    const sortGroupProductsAZ = useCallback(() => {
        updateGroupProperty(group.id, (prevGroup) => {
            const products = [...prevGroup.products];
            products.sort((a, b) => stringComparator(a.partNo ?? '', b.partNo ?? ''));
            return {...prevGroup, products: products};

        })
    }, [updateGroupProperty, group.id]);

    /**
     * Updates a product's property (e.g., note).
     */
    const updateProductProperty = useCallback((productId, updates) => {
        updateGroupProperty(group.id, (prevGroup) => {
            const products = [...prevGroup.products];
            const productIndex = products.findIndex(p => p.id === productId);
            if (productIndex === -1) return prevGroup;
            const newProduct = typeof updates === 'function' ? updates(products[productIndex]) : {...products[productIndex], ...updates};
            return {
                ...prevGroup,
                products: products.map(p => (p.id === productId) ? newProduct : p)
            };
        })
    }, [updateGroupProperty, group.id]);


    return (
        <Card
            variant={'outlined'}
            sx={{
                padding: 2, width: '100%',
                ...(group.isRemoved && {
                    borderStyle: 'dashed',
                    backgroundColor: 'transparent',
                    color: 'text.disabled',
                }),
            }}
            className={'configure-brochure-group-card'}
            data-group-id={group.id}
        >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                <DragIndicator className={'group-handle cursor-move text-color-3'}/>
                <ColorPickerPopover
                    className={'color-circle-container'}
                    color={group.colorCode}
                    onImmediateChange={() => void 0}
                    onSelect={(c) => updateGroupProperty(group.id, {
                        colorCode: c,
                        contrastColorCode: theme.palette.getContrastText(c),
                    })}
                >
                    {(onEnter) =>
                        <Tooltip title={
                            <Box sx={{p: 1, maxWidth: 300}}>
                                {
                                    group.colorCode &&
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                        <Typography variant={'body2'}>
                                            Color:
                                        </Typography>
                                        <div className={'color-circle'}>
                                            <NoColorIcon className={'placeholder'}/>
                                            <div className={'circle'} style={{'--color': group.colorCode}}/>
                                        </div>
                                        <Typography variant={'body2'}>
                                            {group.colorCode}
                                        </Typography>
                                    </Box>
                                }
                                {
                                    !group.colorCode &&
                                    <Typography variant={'body2'}>
                                        No color selected.
                                    </Typography>
                                }
                                {
                                    group.contrastColorCode &&
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                        <Typography variant={'body2'}>
                                            Contrast Color:
                                        </Typography>
                                        <div className={'color-circle'}>
                                            <NoColorIcon className={'placeholder'}/>
                                            <div className={'circle'} style={{'--color': group.contrastColorCode}}/>
                                        </div>
                                        <Typography variant={'body2'}>
                                            {group.contrastColorCode}
                                        </Typography>
                                    </Box>
                                }
                            </Box>
                        }>
                            <div
                                className={classnames('color-circle', {'cursor-pointer': !group.isRemoved})}
                                onClick={(...args) => {
                                    if (group.isRemoved || updatingStep) return;
                                    onEnter(...args);
                                }}
                            >
                                <NoColorIcon className={'placeholder'}/>
                                <div className={'circle'} style={{'--color': group.colorCode}}/>
                            </div>
                        </Tooltip>
                    }
                </ColorPickerPopover>
                <div className={'flex-grow-1 mr-3'}>
                    <Input
                        disableUnderline
                        fullWidth
                        className={'text-md font-weight-bold'}
                        value={group.title ?? ""}
                        onChange={(e) => updateGroupProperty(group.id, {title: e.target.value ?? ""})}
                        placeholder="Group Title"
                        sx={{
                            ...group.isRemoved && {
                                color: 'text.disabled',
                            },
                            'input': {
                                textOverflow: 'ellipsis',
                            },
                        }}
                    />
                </div>

                <Tooltip title={isSorted ? "Products Sorted A-Z" : "Sort Products A-Z"}>
                    <Box component={'span'}>
                        <IconButton
                            onPressed={() => {
                                if (!isSorted) {
                                    sortGroupProductsAZ(group.id);
                                }
                            }}
                            disabled={updatingStep || group.isRemoved}
                            color={'paper.primary'}
                            sx={{
                                ...(!!isSorted && {
                                    backgroundColor: 'primary.main',
                                    color: 'primary.contrastText',
                                    '&:hover': {
                                        backgroundColor: 'primary.dark',
                                        color: 'primary.contrastText',
                                    },
                                })
                            }}
                        >
                            <SortByAlpha className={'icon normal'}/>
                        </IconButton>
                    </Box>
                </Tooltip>

                <Tooltip title={collapsed ? "Expand" : "Collapse"}>
                    <Box component={'span'}>
                        <IconButton onPressed={() => setCollapsed(!collapsed)} color={'secondary'}>
                            <ExpandMore className={classnames('icon normal transition-transform', {'rotate-180': !collapsed})}/>
                        </IconButton>
                    </Box>
                </Tooltip>

                <Tooltip title={group.isRemoved ? "Restore Group" : "Remove Group"}>
                    <Box component={'span'}>
                        <IconButton
                            onPressed={() => updateGroupProperty(group.id, {isRemoved: !group.isRemoved,})}
                            disabled={updatingStep}
                            color={group.isRemoved ? 'error' : 'paper.primary'}
                        >
                            {
                                group.isRemoved
                                    ? <AddOutlined className='icon small'/>
                                    : <RemoveOutlined className='icon small'/>
                            }
                        </IconButton>
                    </Box>
                </Tooltip>
            </Box>

            <Collapse in={!collapsed} mountOnEnter unmountOnExit timeout={'auto'} className={'mt-2'} sx={{width: '100%'}}>
                <Card sx={{marginBlockStart: 2, backgroundColor: 'background.default', padding: 1, paddingBlock: 2, maxHeight: 500, overflowY: 'auto', position: 'relative'}}>
                    <Box
                        component={ReactSortable}
                        sx={{display: 'flex', flexDirection: 'column', minHeight: 200}}
                        list={group.products}
                        // in here we ensure that after dragging, the moved product gets the correct groupId of the new group
                        setList={(newList) => updateGroupProperty(group.id, {products: newList.map(e => ({...e, groupId: group.id}))})}
                        group="shared-products-group"
                        handle={'.product-handle'}
                        animation={150}
                        direction={"vertical"}
                    >
                        {
                            group.products.map(product => (
                                <ProductCard
                                    key={product.id}
                                    product={product}
                                    updateProductProperty={updateProductProperty}
                                    updatingStep={updatingStep}
                                />
                            ))
                        }
                    </Box>
                    {
                        group.products.length === 0 &&
                        <Typography
                            variant={'body2'}
                            textAlign={'center'}
                            tabIndex={-1}
                            sx={{height: 232, position: 'absolute', width: '100%', top: 0, left: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',}}
                        >
                            No products in this group. Drag products here.
                        </Typography>
                    }
                </Card>
            </Collapse>
        </Card>
    );
};


const ProductCard = ({product, updateProductProperty, updatingStep}) => {
    const [note, setNote] = useState(product.note ?? '');

    /**
     * Sync the local note state when [product.note] changes.
     */
    useLayoutEffect(() => {
        setNote(product.note ?? '');
    }, [product.note]);

    return (
        <>
            <Card
                variant={'outlined'}
                key={product.id}
                sx={{
                    padding: 1, width: '100%', marginBottom: 1.5,
                    ...(product.isRemoved && {
                        borderStyle: 'dashed',
                        backgroundColor: 'transparent',
                        color: 'text.disabled',
                    }),
                }}
            >
                <div className={'print-catalogue-product-card d-flex flex-column h-100'}>
                    <div className={'d-flex align-items-center '}>
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center',}}>
                            {
                                !product.isRemoved &&
                                <DragHandleRounded className={'product-handle cursor-move text-color-3'}/>
                            }
                        </Box>
                        <div
                            className={'product-image mr-2'}
                            style={{backgroundImage: `url(${Utils.encodeFileUrl(product.coverImageFileName)})`}}
                        />
                        <Box sx={{flexGrow: 1, display: 'flex', alignItems: 'center'}}>
                            <ProductInformationPopover partNo={product.partNo}>
                                {product.partNo}
                            </ProductInformationPopover>
                        </Box>
                        <IconButton
                            onPressed={() => updateProductProperty(product.id, {isRemoved: !product.isRemoved})}
                            disabled={updatingStep}
                            color={product.isRemoved ? 'error' : 'paper.primary'}
                        >
                            {
                                product.isRemoved
                                    ? <AddOutlined className='icon small'/>
                                    : <RemoveOutlined className='icon small'/>
                            }
                        </IconButton>
                    </div>
                    <div className={'mt-auto p-2'}>
                        <TextField
                            variant="standard"
                            size="small"
                            fullWidth
                            placeholder="Add a note..."
                            value={note}
                            disabled={updatingStep || product.isRemoved}
                            onChange={(e) => setNote(e.target.value)}
                            onBlur={() => updateProductProperty(product.id, {note: note})}
                            InputProps={{
                                disableUnderline: true,
                                className: 'text-xs',
                                sx: {backgroundColor: 'transparent'},
                            }}
                            multiline
                            maxRows={4}
                        />
                    </div>
                </div>
            </Card>
        </>
    )
}

export default PrintBrochureViewConfigureGroupsSection;
